import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Send, Loader2, Bot, User as UserIcon, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const SUGGESTIONS = [
  "Quanti operai sono presenti oggi e su quali cantieri?",
  "Quali fatture scadono nei prossimi 7 giorni?",
  "Qual è il cantiere con il margine peggiore questo mese?",
  "Elenca i rendiconti in attesa di approvazione.",
  "Quanto ho fatturato in acquisti vs vendite questo mese?",
];

export default function AIAssistente() {
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]); // {role, text}
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  const scrollToBottom = () => {
    requestAnimationFrame(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    });
  };

  useEffect(() => { scrollToBottom(); }, [messages, busy]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    setBusy(true);
    setMessages((m) => [...m, { role: "user", text: q }]);
    setInput("");
    try {
      const { data } = await api.post("/ai/chat", { message: q, session_id: sessionId });
      if (data?.session_id) setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", text: data?.answer || "Nessuna risposta." }]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore AI");
      setMessages((m) => [...m, { role: "assistant", text: "Errore nella richiesta. Riprova." }]);
    } finally {
      setBusy(false);
    }
  };

  const resetChat = () => {
    setMessages([]);
    setSessionId(null);
    toast.info("Nuova conversazione");
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Gemini AI</div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight flex items-center gap-2">
            <Sparkles className="h-7 w-7 text-primary" /> Chiedi a Cantiere360
          </h1>
          <p className="text-muted-foreground mt-1">
            L'AI consulta i tuoi cantieri, operai, fatture e scadenze in tempo reale.
          </p>
        </div>
        {messages.length > 0 && (
          <Button variant="outline" size="sm" onClick={resetChat} data-testid="ai-reset">
            <RefreshCw className="h-3 w-3 mr-2" /> Nuova chat
          </Button>
        )}
      </div>

      <Card className="card-elevated">
        <CardContent className="p-0">
          <div ref={scrollRef} className="h-[55vh] md:h-[60vh] overflow-y-auto p-4 md:p-6 space-y-4 bg-background">
            {messages.length === 0 && (
              <div className="space-y-6">
                <div className="text-center py-8 space-y-3">
                  <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                    <Bot className="h-7 w-7 text-primary" />
                  </div>
                  <div className="text-lg font-bold">Chiedimi qualunque cosa sulla tua azienda</div>
                  <div className="text-sm text-muted-foreground max-w-md mx-auto">
                    Rispondo basandomi sui dati reali di Cantiere360: cantieri, fatture, presenze, scadenze, DDT.
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground px-2">Prova a chiedere</div>
                  {SUGGESTIONS.map((s, i) => (
                    <button
                      key={i}
                      data-testid={`ai-suggestion-${i}`}
                      onClick={() => send(s)}
                      disabled={busy}
                      className="w-full text-left px-4 py-3 rounded-lg bg-secondary hover:bg-secondary/70 text-sm transition-colors border border-transparent hover:border-primary"
                    >
                      <Sparkles className="h-3 w-3 inline mr-2 text-primary" />{s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <div key={i} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`} data-testid={`ai-msg-${i}`}>
                <div className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 ${m.role === "user" ? "bg-primary text-primary-foreground" : "bg-secondary"}`}>
                  {m.role === "user" ? <UserIcon className="h-4 w-4" /> : <Bot className="h-4 w-4 text-primary" />}
                </div>
                <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap leading-relaxed ${m.role === "user" ? "bg-primary text-primary-foreground" : "bg-card border border-border"}`}>
                  {m.text}
                </div>
              </div>
            ))}

            {busy && (
              <div className="flex gap-3">
                <div className="h-8 w-8 rounded-full bg-secondary flex items-center justify-center shrink-0">
                  <Bot className="h-4 w-4 text-primary" />
                </div>
                <div className="bg-card border border-border rounded-2xl px-4 py-3 text-sm flex items-center gap-2">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  <span className="text-muted-foreground">sto pensando…</span>
                </div>
              </div>
            )}
          </div>

          <div className="border-t border-border p-3 bg-card">
            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2">
              <Input
                data-testid="ai-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Scrivi la tua domanda..."
                disabled={busy}
                className="h-11"
              />
              <Button data-testid="ai-send" type="submit" disabled={busy || !input.trim()} className="h-11 px-6">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </form>
            <div className="text-[10px] text-muted-foreground mt-2 text-center">
              <Badge variant="secondary" className="text-[10px]">Powered by Gemini 2.5</Badge>
              {" "}· I dati non lasciano mai l'azienda, l'AI vede solo quello che è nel tuo database Cantiere360.
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
